import React from 'react';
import { Column } from 'react-table';
import { FormattedMessage } from 'react-intl';
import Highlighter from 'react-highlight-words';
import { ICheckboxFilterItem } from '../../../../common/models/filter/ICheckboxFilterItem';
import { CandidateFilterType } from '../../../../common/enums/CandidateFilterType';
import { SortType } from '../../../../common/enums/SortType';
import { formatDate } from '../../../../common/helpers/dateHelper';
import { ICandidate } from '../../../../common/models/candidate/ICandidate';
import CustomBadge from '../../../../components/CustomBadge';
import UserLogo from '../../../../components/UserLogo';
import { DashboardColor } from '../../../../common/enums/DashboardColor';
import Icon from '../../../../components/Icon';
import { CandidateStatus } from '../../../../common/enums/CandidateStatus';
import { inactiveProgressMessage } from '../../../../common/helpers/dashboardHelper';
import { IBindingCallback1 } from '../../../../common/models/callback';
import IconButton from '../../../../components/IconButton';
import TextOverflowTooltip from '../../../../components/TextOverflowTooltip';
import EmptyFieldPlaceholder from '../../../../components/EmptyFieldPlaceholder';

const referenceProgress = (item: ICandidate) => {
  if (item.needsReview) {
    return (
      <div className="custom-badge red">
        <FormattedMessage id="needsReview" defaultMessage="Needs review" />
      </div>
    );
  }
  return (item.totalReferences > 0
    ? (
      <CustomBadge min={1} value={item.referenceProgress} max={item.totalReferences} status={item.status}>
        {
          item.status !== CandidateStatus.Active ? inactiveProgressMessage(item.status) : (
            <FormattedMessage id="complete" defaultMessage="Complete">
              {message => `${item.referenceProgress} / ${item.totalReferences} ${message}`}
            </FormattedMessage>
          )
        }
      </CustomBadge>
    )
    : (
      <CustomBadge min={1} value={0} max={2} status={item.status}>
        {
          item.status !== CandidateStatus.Active ? inactiveProgressMessage(item.status) : (
            <FormattedMessage id="candidateSidebarFilter.WaitingForCandidate" defaultMessage="Waiting For Candidate" />
          )
        }
      </CustomBadge>
    ));
};

export const getColumns = (
  search: string,
  archive: IBindingCallback1<string>,
  unarchive: IBindingCallback1<string>,
  runReport: IBindingCallback1<string>
): Column[] => [
  {
    Header: () => <FormattedMessage id="candidateTable.requestDate" defaultMessage="Request date" />,
    accessor: 'requestDate',
    width: 135,
    Cell: ({ cell: { value } }) => value && formatDate(value),
    sortType: SortType.Date
  },
  {
    Header: () => <FormattedMessage id="candidateTable.candidateName" defaultMessage="Candidate name" />,
    id: 'name',
    Cell: ({ row: { original } }: any) => (
      <TextOverflowTooltip content={`${original.lastName}, ${original.firstName}`} width={170}>
        <Highlighter
          highlightClassName="highlighted-text"
          searchWords={[search]}
          textToHighlight={`${original.lastName}, ${original.firstName}`}
          className="cell-active"
          autoEscape
        />
      </TextOverflowTooltip>
    )
  },
  {
    Header: () => <FormattedMessage id="candidateTable.referenceProgress" defaultMessage="Reference progress" />,
    id: 'referenceProgress',
    accessor: (item: any) => referenceProgress(item),
    sortType: SortType.Numeric,
    disableFilters: true,
    width: 180
  },
  {
    Header: () => <FormattedMessage id="candidateTable.position" defaultMessage="Position" />,
    id: 'position',
    width: 180,
    Cell: ({ row: { original } }) => {
      const { position } = original as ICandidate;

      return (position
        ? (
          <TextOverflowTooltip content={position} width={180}>
            <Highlighter
              highlightClassName="highlighted-text"
              searchWords={[search]}
              textToHighlight={position}
              className="cell-active"
              autoEscape
            />
          </TextOverflowTooltip>
        )
        : <EmptyFieldPlaceholder />
      );
    }
  },
  {
    Header: () => <FormattedMessage id="candidateTable.assignedUser" defaultMessage="Assigned User" />,
    accessor: 'owner',
    id: 'owner',
    Cell: ({ cell: { value } }) => (
      <div style={{ maxWidth: '90px' }} className="table-center cursor-pointer">
        {value ? <UserLogo user={value} /> : <EmptyFieldPlaceholder />}
      </div>
    ),
    width: 125
  },
  {
    Header: () => <FormattedMessage id="actions" defaultMessage="Actions" />,
    id: 'actions',
    disableSortBy: true,
    disableFilters: true,
    accessor: '-',
    width: 80,
    Cell: ({ row: { original } }) => {
      const { referenceProgress: referenceProgressVal, id, status } = original as ICandidate;
      const isActive = status === CandidateStatus.Active;
      return (
        <div style={{ maxWidth: '50px' }} className="table-actions table-center">
          <IconButton
            icon={<Icon icon="chart" />}
            onClick={() => runReport(id)}
            disabled={!referenceProgressVal}
            variant="blue"
            tooltip={{ id: 'runReport', defaultMessage: 'Run report' }}
          />
          <IconButton
            onClick={() => (isActive ? archive(id) : unarchive(id))}
            icon={<Icon icon={isActive ? 'archive' : 'unarchive'} />}
            variant="blue"
            tooltip={isActive
              ? { id: 'iconButtonLable.archive', defaultMessage: 'Archive / Inactive' }
              : { id: 'iconButtonLable.unarchive', defaultMessage: 'Unarchive / Active' }}
          />
        </div>
      );
    }
  }
];

export const sidebarItems: ICheckboxFilterItem[] = [{
  id: CandidateFilterType.AllActive,
  name: 'candidateSidebarFilter.AllActive',
  count: 0,
  color: DashboardColor.Neutral,
  children: [{
    id: CandidateFilterType.WaitingForCandidate,
    name: 'candidateSidebarFilter.WaitingForCandidate',
    color: DashboardColor.Neutral,
    count: 0
  } as ICheckboxFilterItem,
    {
      id: CandidateFilterType.NoSurveysComplete,
      name: 'candidateSidebarFilter.NoSurveysComplete',
      color: DashboardColor.BrightNeutral,
      count: 0
    } as ICheckboxFilterItem,
    {
      id: CandidateFilterType.OnePlusSurveysComplete,
      name: 'candidateSidebarFilter.OnePlusSurveysComplete',
      color: DashboardColor.Yellow,
      count: 0
    } as ICheckboxFilterItem,
    {
      id: CandidateFilterType.AllSurveysComplete,
      name: 'candidateSidebarFilter.AllSurveysComplete',
      color: DashboardColor.Green,
      count: 0
    } as ICheckboxFilterItem]
} as ICheckboxFilterItem,
  {
    id: CandidateFilterType.AllInactive,
    name: 'candidateSidebarFilter.AllInactive',
    count: 0,
    color: DashboardColor.Neutral,
    children: [{
      id: CandidateFilterType.InactiveHired,
      name: 'candidateSidebarFilter.InactiveHired',
      color: DashboardColor.Blue,
      count: 0
    } as ICheckboxFilterItem,
    {
      id: CandidateFilterType.InactiveHibernate,
      name: 'candidateSidebarFilter.InactiveHibernate',
      color: DashboardColor.Purple,
      count: 0
    } as ICheckboxFilterItem]
  } as ICheckboxFilterItem
];
